import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
    Alert,
    Button,
    Card,
    CardBody,
    CardHeader,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import {
    select_filters_expanded,
    updateFiltersExpanded,
} from '../../redux/modules/filtersExpandedUi';
import FiltersCardRefreshControl from './FiltersCardRefreshControl';

import './FiltersCard.css';

/**
 * Shared filters card used by every list page (posts, programmation,
 * screenings, customers, tickets, stats, analysis...).
 *
 * Collapsed/expanded state is persisted like date periods:
 * - exploitation (screenings, tasks, programmation) → user↔instance prefs
 * - analyses / stats → `kronos.user.<id>.analysesStatsFiltersExpanded`
 * - generic lists → `kronos.user.<id>.listFiltersExpanded`
 */
function FiltersCard({
    title,
    summaryParts,
    onReset,
    resetLabel,
    onRefresh,
    refreshLabel,
    showAutoRefreshControl,
    isLoading,
    actions,
    minActiveFilters,
    activeFiltersCount,
    invalidMessage,
    children,
    className,
    expandedScope,
}) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const expanded = useSelector((s) => select_filters_expanded(s, expandedScope));

    const resolved_title = title ?? t('statstypes.form.filters');
    const resolved_reset_label = resetLabel ?? t('common.reset');
    const summary_parts = Array.isArray(summaryParts) ? summaryParts.filter(Boolean) : [];
    const has_summary = summary_parts.length > 0;

    const isInvalid = minActiveFilters > 0
        && (activeFiltersCount ?? 0) < minActiveFilters
        && Boolean(invalidMessage);

    const showExpanded = expanded || isInvalid;

    useEffect(() => {
        if (isInvalid && !expanded) {
            dispatch(updateFiltersExpanded(expandedScope, true));
        }
    }, [dispatch, expanded, expandedScope, isInvalid]);

    const toggleExpanded = () => {
        if (isInvalid) {
            return;
        }
        dispatch(updateFiltersExpanded(expandedScope, !expanded));
    };

    /* The whole header (top of the card, including title and summary) is
     * clickable and toggles the collapse state — same UX as the pricings
     * cards in the ticket types screen. Interactive children (Reset
     * button, +/- toggle, custom `actions`) stop propagation so clicking
     * them does not also toggle. */
    const on_header_click = () => toggleExpanded();
    const on_header_key = (e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleExpanded();
        }
    };
    const stop_click = (e) => e.stopPropagation();

    return (
        <div className={`FiltersCard-wrapper ${className || ''}`.trim()}>
            <Card className="mb-2 FiltersCard">
                <CardHeader
                    tag="div"
                    className="FiltersCard__header d-flex align-items-center flex-wrap justify-content-start text-left"
                    style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                    onClick={on_header_click}
                    onKeyDown={on_header_key}
                    role="button"
                    tabIndex={0}
                    aria-expanded={showExpanded}
                    title={t(showExpanded ? 'common.collapse' : 'common.expand')}
                >
                    <div
                        className="FiltersCard__header-lead d-flex align-items-baseline flex-wrap"
                        style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                    >
                        <span className="FiltersCard__title-label">
                            <i className="fa fa-filter" aria-hidden="true"></i>&nbsp;{resolved_title}
                        </span>
                        {!showExpanded && has_summary && (
                            <span
                                className="FiltersCard__summary text-muted ml-2"
                                title={summary_parts.join('\n')}
                            >
                                {summary_parts.join(' · ')}
                            </span>
                        )}
                    </div>
                    <span
                        className="ml-auto d-flex align-items-stretch gap-2 FiltersCard__header-tools"
                        onClick={stop_click}
                    >
                        {typeof onReset === 'function' && (
                            <Button
                                type="button"
                                size="sm"
                                color="secondary"
                                outline
                                className="FiltersCard__toolbar-btn FiltersCard__header-tool text-nowrap"
                                onClick={onReset}
                                title={resolved_reset_label}
                            >
                                <i className="fa fa-undo" aria-hidden="true"></i>&nbsp;{resolved_reset_label}
                            </Button>
                        )}
                        <span onClick={stop_click}>
                            <FiltersCardRefreshControl
                                onRefresh={onRefresh}
                                refreshLabel={refreshLabel}
                                showAutoRefreshControl={showAutoRefreshControl}
                                isLoading={isLoading}
                            />
                        </span>
                        {actions}
                        <Button
                            type="button"
                            size="sm"
                            color="success"
                            outline
                            className="FiltersCard__toolbar-btn FiltersCard__header-tool"
                            onClick={toggleExpanded}
                            aria-expanded={showExpanded}
                            title={t(showExpanded ? 'common.collapse' : 'common.expand')}
                        >
                            <i
                                className={`fa fa-chevron-right FiltersCard__expand-chevron${showExpanded ? ' FiltersCard__expand-chevron--open' : ''}`}
                                aria-hidden="true"
                            />
                        </Button>
                    </span>
                </CardHeader>
                {showExpanded && (
                    <CardBody className="FiltersCard__body">
                        {children}
                    </CardBody>
                )}
            </Card>
            {isInvalid && (
                <Alert color="info" className="FiltersCard__invalid-alert">
                    <i className="fa fa-info-circle" aria-hidden="true"></i>&nbsp;
                    {invalidMessage}
                </Alert>
            )}
        </div>
    );
}

FiltersCard.propTypes = {
    title: PropTypes.node,
    summaryParts: PropTypes.arrayOf(PropTypes.node),
    onReset: PropTypes.func,
    resetLabel: PropTypes.node,
    onRefresh: PropTypes.func,
    refreshLabel: PropTypes.node,
    showAutoRefreshControl: PropTypes.bool,
    isLoading: PropTypes.bool,
    actions: PropTypes.node,
    minActiveFilters: PropTypes.number,
    activeFiltersCount: PropTypes.number,
    invalidMessage: PropTypes.node,
    children: PropTypes.node,
    className: PropTypes.string,
    expandedScope: PropTypes.oneOf([
        'exploitation.screenings',
        'exploitation.tasks',
        'exploitation.programmation',
        'analyses',
        'lists',
    ]).isRequired,
};

FiltersCard.defaultProps = {
    title: null,
    summaryParts: [],
    onReset: undefined,
    resetLabel: null,
    onRefresh: undefined,
    refreshLabel: null,
    showAutoRefreshControl: true,
    isLoading: false,
    actions: null,
    minActiveFilters: 0,
    activeFiltersCount: 0,
    invalidMessage: null,
    children: null,
    className: '',
};

/**
 * Helper hook: given a computed `invalidMessage` flag (activeFiltersCount <
 * minActiveFilters), pages can call `useFiltersCardValidation` to decide
 * whether to skip the XHR fetch. Prefer this over duplicating the condition.
 */
export function useFiltersCardValidation({ minActiveFilters = 0, activeFiltersCount = 0 } = {}) {
    return (activeFiltersCount ?? 0) >= (minActiveFilters ?? 0);
}

export default FiltersCard;
